import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { RecommendationInterface, RecommendationScenarioType } from '../../+models';
import * as RecommendationActions from './recommendations.actions';

export const NAME = 'recommendations';

export interface State extends EntityState<RecommendationInterface> {
  error?: any;
  byTocId: Partial<Record<number, string[]>>; // map of tocId to conceptGuids
  byScenario: Partial<Record<RecommendationScenarioType, string[]>>; // map of strategy to conceptGuids
}

export const adapter: EntityAdapter<RecommendationInterface> = createEntityAdapter<RecommendationInterface>({
  selectId: (entity) => entity.conceptGuid,
});

export const initialState: State = adapter.getInitialState({
  byTocId: {},
  byScenario: {
    'difficult-lessons': null,
    drilling: null,
  },
});

export const reducer = createReducer(
  initialState,
  on(RecommendationActions.recommendationsForContextLoaded, (state, { context, recommendations = [] }) => {
    const { tocId, scenario } = context;

    // Two parallel scenario calls (`drilling` + `difficult-lessons`) can return the same concept.
    // Only the `drilling` response carries `content.publishedEduContentMetadata.data`, so if the
    // `difficult-lessons` response arrives last its shallow upsert would wipe `data` out. Preserve it.
    const mergedRecommendations = recommendations.map((rec) => preserveData(rec, state.entities[rec.conceptGuid]));

    if (tocId) {
      return adapter.upsertMany(mergedRecommendations, {
        ...state,
        byTocId: {
          ...state.byTocId,
          [tocId]: recommendations.map((rec) => rec.conceptGuid),
        },
        byScenario: initialState.byScenario,
      });
    }

    if (scenario) {
      return adapter.upsertMany(mergedRecommendations, {
        ...state,
        byScenario: {
          ...state.byScenario,
          [scenario]: recommendations.map((rec) => rec.conceptGuid),
        },
        byTocId: initialState.byTocId,
      });
    }
  }),
  on(RecommendationActions.recommendationsForContextLoadError, (state, { error }) => ({
    ...state,
    error,
  }))
);

export const { selectAll, selectEntities } = adapter.getSelectors();

function preserveData(
  incoming: RecommendationInterface,
  existing: RecommendationInterface | undefined
): RecommendationInterface {
  const existingData = existing?.content?.publishedEduContentMetadata?.data;
  if (!existingData) return incoming;
  if (incoming.content?.publishedEduContentMetadata?.data) return incoming;
  if (!incoming.content) return incoming;

  return {
    ...incoming,
    content: {
      ...incoming.content,
      publishedEduContentMetadata: {
        ...incoming.content.publishedEduContentMetadata,
        data: existingData,
      },
    },
  };
}
